import { IField, structuredCloneL } from '@eventr/common'
import {
    ColoursObj,
    Gutters,
    Icon,
    MainButton,
    MobileConfirmDialog,
    TDialogStore,
    useDialogStore,
    useValidatorContext,
} from '@eventr/common-storybook'
import { Box, Stack } from '@mui/material'
import { useSignal } from '@preact/signals-react'
import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router'
import { useShallow } from 'zustand/react/shallow'
import { IEditComponent } from '../../data/Data'
import i18n from '../../i18n/i18next'
import { bs, useBoundStore } from '../../state/bound.store'
import { defaultAddSnack } from '../../state/overlayState/overlayContext'
import { NavigationView } from '../layout/NavigationView'
import { ToolbarItemProps } from '../layout/TopToolbar'
import { ComponentFinderEdit } from './componentFinderEdit'
import { useEditSectionContext } from './editSectionProvider'

export const EditItem = () => {
    const {
        section,
        onCancelEdit,
        onSaveData,
        anyPendingChanges,
        isNewItem,
        getValue,
        nowSetAlreadyPopulated,
        values,
        deleteItem,
    } = useEditSectionContext()
    const { replaceDialog, clearDialog }: TDialogStore = useDialogStore(
        useShallow((s: TDialogStore) => ({
            clearDialog: s.clearDialog,
            replaceDialog: s.replaceDialog,
        }))
    )

    const ogValues = useSignal({})

    const { idItem } = useParams<{
        idSection: string
        idItem: string
    }>()
    const { setValidationOn, setShowErrors } = useValidatorContext()

    useEffect(() => {
        setValidationOn(true)
    }, [])

    useEffect(() => {
        if (isNewItem(idItem)) {
            ogValues.value = structuredCloneL(values)
        }
    }, [])

    const componentsToRender = useMemo(() => {
        return section?.properties?.mobileEditFieldOptions?.components || []
    }, [section])

    const deleteButtonText = () =>
        i18n.t('edit-itinerary.item.delete-booking-section', {
            sectionName: i18n.t(`sections.${section.properties.jsonName}`, {
                count: 1,
            }),
        }) || 'Delete Item'

    const headerText = () => {
        const newItem = isNewItem(idItem)
        if (newItem) {
            return (
                i18n.t('edit-itinerary.item.add-booking', {
                    sectionName: i18n.t(
                        `sections.${section.properties.jsonName}`,
                        {
                            count: 1,
                        }
                    ),
                }) || i18n.t('edit-itinerary.item.edit-new')
            )
        } else {
            const headerKey =
                section?.properties?.mobileEditFieldOptions?.headerKeyValue
            if (!headerKey) {
                return i18n.t('edit-itinerary.item.edit-new')
            }

            const field = useBoundStore
                .getState()
                .getFieldsForSection(section.id)
                ?.find(
                    (field: IField) =>
                        Number.parseInt(field.id.toString()) === headerKey
                )
            if (!field) {
                return i18n.t('edit-itinerary.item.edit-new')
            }
            const keyFieldValue = getValue(field, idItem)

            return i18n.t('general.edit') + ' ' + keyFieldValue
        }
    }

    const onDelete = async () => {
        if (
            await useBoundStore
                .getState()
                .deleteItineraryItem(section.id, idItem)
        ) {
            clearDialog()
            setShowErrors(false)
            setValidationOn(false)
            deleteItem(idItem)
            window.replacePath('/edit/Section/' + section.id)
        } else {
            clearDialog()
            defaultAddSnack(i18n.t('snack.not-deleted-correctly'), 'error')
        }
    }

    const onDeleteClick = () => {
        replaceDialog({
            node: (
                <MobileConfirmDialog
                    text={i18n.t('edit-itinerary.item.delete-booking-text')}
                    header={i18n.t('edit-itinerary.item.delete-booking')}
                    onConfirm={onDelete}
                    onCancel={() => {
                        clearDialog()
                    }}
                    cancelText={i18n.t('general.cancel')}
                    confirmText={i18n.t('general.delete')}
                />
            ),
            backdropClosable: false,
        })
    }

    const onBackPressed = () => {
        if (anyPendingChanges) {
            replaceDialog({
                node: (
                    <MobileConfirmDialog
                        text={i18n.t(
                            'edit-itinerary.item.unsaved-booking-text'
                        )}
                        header={i18n.t('edit-itinerary.item.unsaved-booking')}
                        onCancel={() => {
                            clearDialog()
                        }}
                        onConfirm={handleDialogConfirm}
                        cancelText={i18n.t('general.cancel')}
                        confirmText={i18n.t('general.leave')}
                    />
                ),
                backdropClosable: false,
            })
        } else {
            setShowErrors(false)
            setValidationOn(false)
            nowSetAlreadyPopulated(false)
            window.goBack()
        }
    }

    const handleDialogConfirm = () => {
        clearDialog()
        setShowErrors(false)
        setValidationOn(false)
        nowSetAlreadyPopulated(false)
        onCancelEdit()
        window.goBack()
    }

    const getItinTitle = () => {
        const eventInfo = bs().getEventInfoV2()
        return eventInfo?.data.title
    }
    if (!section)
        return (
            <Stack justifyContent={'center'} direction={'row'}>
                <Icon name="Loading" />
            </Stack>
        )

    const backButton: ToolbarItemProps = {
        id: 'backButtonNavigation',
        buttonText: i18n.t('general.cancel'),
        useButton: true,
        colour: ColoursObj.core.primary,
        action: () => {
            onBackPressed()
        },
    }
    return (
        <>
            <NavigationView
                usePageTransition
                navigationBarTitle={headerText()}
                navigationBarSubtitle={getItinTitle()}
                backButton={backButton}
                backButtonAction={onBackPressed}
                navigationBarTrailing={[
                    {
                        id: 'saveButton',
                        buttonText: i18n.t('general.done'),
                        colour: ColoursObj.core.primary,
                        useButton: true,
                        action: () => {
                            onSaveData(idItem)
                        },
                    },
                ]}
            >
                <Gutters>
                    <Stack gap="16px">
                        <Stack spacing="16px">
                            {section &&
                                idItem &&
                                componentsToRender.length > 0 &&
                                componentsToRender.map(
                                    (option: IEditComponent, index: number) => {
                                        return (
                                            <Stack key={index}>
                                                <ComponentFinderEdit
                                                    _id={idItem}
                                                    editComponent={option}
                                                />
                                            </Stack>
                                        )
                                    }
                                )}
                        </Stack>
                        {section?.id !== 1 && !isNewItem(idItem) ? (
                            <Stack
                                gap="16px"
                                direction="row"
                                justifyContent="center"
                            >
                                <MainButton
                                    id="deleteItemButton"
                                    style={{ width: '100%' }}
                                    text={deleteButtonText()}
                                    variant="destructiveBorderless"
                                    onClick={onDeleteClick}
                                />
                            </Stack>
                        ) : (
                            <Box height={'16px'} />
                        )}
                    </Stack>
                </Gutters>
            </NavigationView>
        </>
    )
}
